import { jwtDecode } from "jwt-decode";
import { safeLocalStorage } from "util/safeStore";

interface JwtPayload {
  exp: number;
  [key: string]: unknown;
}

const TOKEN_KEY = "accessToken";
// Treat a token as expired this many seconds early to avoid mid-request expiry.
const EXPIRY_BUFFER_SECONDS = 60;
const MAX_REQUEST_ATTEMPTS = 3;
const TOKEN_TIMEOUT_MS = 10000;

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    if (!decoded.exp) return true;
    return decoded.exp < Date.now() / 1000 + EXPIRY_BUFFER_SECONDS;
  } catch (error) {
    console.warn("Token decode error:", error);
    return true;
  }
};

const getStoredToken = (): string | null => safeLocalStorage.getItem(TOKEN_KEY);

const storeToken = (token: string): void => {
  safeLocalStorage.setItem(TOKEN_KEY, token);
};

const postToParent = (message: Record<string, unknown>): void => {
  try {
    // Use "*" for cross-origin communication
    window.parent.postMessage(message, "*");
  } catch (error) {
    console.warn("postMessage to parent failed:", error);
  }
};

type AuthFailedListener = () => void;
const authFailedListeners = new Set<AuthFailedListener>();

// Lets the Auth gate flip to its failure screen when a LATE getValidAccessToken
// call resolves null (token expired mid-session, parent cannot refresh it),
// without every call site wiring its own error handling
export const onAuthFailed = (listener: AuthFailedListener): (() => void) => {
  authFailedListeners.add(listener);
  return () => {
    authFailedListeners.delete(listener);
  };
};

const notifyAuthFailed = (): void => {
  postToParent({ type: "AUTH_FAILED", reason: "timeout", timestamp: Date.now() });
  authFailedListeners.forEach((listener) => listener());
};

const requestTokenFromParent = (attempt: number): void => {
  if (attempt >= MAX_REQUEST_ATTEMPTS) {
    console.warn("Max token request attempts reached");
    return;
  }

  console.log(`Requesting token from parent (attempt ${attempt + 1})`);
  postToParent({ type: "NEW_AUTH_TOKEN", attempt: attempt + 1, timestamp: Date.now() });

  setTimeout(
    () => {
      const token = getStoredToken();
      if (!token || isTokenExpired(token)) {
        requestTokenFromParent(attempt + 1);
      }
    },
    1000 * (attempt + 1),
  );
};

// One in-flight acquisition shared by concurrent callers, and the resolver it will settle.
let inflight: Promise<string | null> | null = null;
let pendingResolver: ((value: string | null) => void) | null = null;

// Acquire a token from the parent. Concurrent callers share a single in-flight
// request. Resolves the token, or null on timeout. Has no side effects: callers
// decide whether a null result warrants notifying the parent.
const acquireToken = async (): Promise<string | null> => {
  // if an inflight already exists it's safe to return it
  // without awaiting because the previous call to acquireToken
  // would have set the try/finally block for it
  if (inflight) return inflight;

  inflight = new Promise<string | null>((resolve) => {
    pendingResolver = resolve;

    requestTokenFromParent(0);

    setTimeout(() => {
      if (pendingResolver) {
        console.warn("No valid token received within timeout");
        const settle = pendingResolver;
        pendingResolver = null;
        settle(null);
      }
    }, TOKEN_TIMEOUT_MS);
  });

  // make sure to await this inflight so that the finally
  // block resets inflight & pendingResolver to null
  try {
    return await inflight;
  } finally {
    inflight = null;
    pendingResolver = null;
  }
};

// Proactive refresh timer state.
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let scheduledRefreshExp: number | null = null;

// Arm a background refresh to fire shortly before the token expires, so a fresh
// token is usually available without a reactive fetch. No-op if already armed
// for this token's expiry. The refresh is best-effort and dedupes with any
// in-flight manual fetch.
const scheduleRefresh = (token: string): void => {
  let exp: number | undefined;
  try {
    exp = jwtDecode<JwtPayload>(token).exp;
  } catch {
    return;
  }
  if (!exp) return;

  if (scheduledRefreshExp === exp && refreshTimer !== null) return;

  if (refreshTimer !== null) clearTimeout(refreshTimer);
  scheduledRefreshExp = exp;

  const delayMs = (exp - EXPIRY_BUFFER_SECONDS) * 1000 - Date.now();
  refreshTimer = setTimeout(
    () => {
      refreshTimer = null;
      scheduledRefreshExp = null;
      // Fire-and-forget. This fires ~EXPIRY_BUFFER_SECONDS before expiry, so the
      // current token is still usable if the refresh fails. Stay silent on
      // failure: a truly broken session surfaces AUTH_FAILED via a real
      // getValidAccessToken() call, and notifying here could make the parent tear
      // down a session whose token is still valid.
      void acquireToken();
    },
    Math.max(delayMs, 0),
  );
};

const handleTokenMessage = (event: MessageEvent): void => {
  const token = event.data.token;
  if (!token) {
    console.warn("Received empty token");
    return;
  }
  if (isTokenExpired(token)) {
    console.warn("Received expired token from parent");
    return;
  }

  storeToken(token);
  scheduleRefresh(token);
  console.log("Received valid auth token from parent:", event.origin);

  if (pendingResolver) {
    const resolve = pendingResolver;
    pendingResolver = null;
    resolve(token);
  }
};

// Parent-initiated logout: clear storage and redirect. localStorage.clear() is
// synchronous, so storage is gone before the navigation unloads the page.
const handleLogoutMessage = (event: MessageEvent): void => {
  console.log("Received logout event from parent:", event.origin);
  const redirectURL =
    event.data.baseURL || process.env.REACT_APP_BASE_URL || window.location.origin;
  safeLocalStorage.clear();
  window.location.href = redirectURL;
};

const handleParentMessage = (event: MessageEvent): void => {
  const type = event.data?.type;
  if (type === "AUTH_TOKEN" || type === "NEW_ACCESS_TOKEN") {
    handleTokenMessage(event);
  } else if (type === "LOGOUT" || type === "AUTH_LOGOUT") {
    handleLogoutMessage(event);
  }
};

// Recover from background-tab timer throttling: when the tab is visible again,
// refresh if the token has expired, otherwise re-arm the proactive refresh.
const handleVisibilityChange = (): void => {
  if (document.visibilityState !== "visible") return;

  const stored = getStoredToken();
  if (!stored || isTokenExpired(stored)) {
    void acquireToken();
  } else {
    scheduleRefresh(stored);
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("message", handleParentMessage);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // Arm a refresh for a token persisted from a previous session; tokens
  // acquired this session are armed in handleTokenMessage.
  const stored = getStoredToken();
  if (stored && !isTokenExpired(stored)) scheduleRefresh(stored);
}

/**
 * Returns a valid, unexpired access token. Resolves immediately when a usable
 * token is already stored; otherwise requests one from the parent window and
 * awaits its reply. Concurrent callers share a single in-flight request. If
 * acquisition fails, the parent is notified (AUTH_FAILED) and this resolves null.
 */
export const getValidAccessToken = async (): Promise<string | null> => {
  const stored = getStoredToken();
  if (stored && !isTokenExpired(stored)) {
    return stored;
  }

  const token = await acquireToken();
  if (!token) notifyAuthFailed();
  return token;
};
